import { AnimatePresence, motion } from "framer-motion";
import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Icon } from "./Icon";
import { EASE } from "@/motion/variants";

/* -------------------------------------------------------------------------- */
/* Field wrapper                                                              */
/* -------------------------------------------------------------------------- */

interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, htmlFor, error, hint, required, children }: FieldProps) {
  return (
    <div className={`field${error ? " has-error" : ""}`}>
      <label htmlFor={htmlFor}>
        {label}
        {required ? <span className="req">*</span> : null}
      </label>
      {children}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
      <AnimatePresence>
        {error ? (
          <motion.span
            className="field__error"
            style={{ display: "flex" }}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: EASE }}
            role="alert"
          >
            <Icon name="alert" />
            {error}
          </motion.span>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Inputs                                                                     */
/* -------------------------------------------------------------------------- */

type InputProps = InputHTMLAttributes<HTMLInputElement> & { error?: string };

export const TextInput = forwardRef<HTMLInputElement, InputProps>(function TextInput(
  { error, ...rest },
  ref,
) {
  return <input ref={ref} aria-invalid={error ? true : undefined} {...rest} />;
});

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: string };

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaProps>(function TextArea(
  { error, ...rest },
  ref,
) {
  return <textarea ref={ref} aria-invalid={error ? true : undefined} {...rest} />;
});

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  error?: string;
  options: readonly string[];
  placeholder?: string;
};

export const SelectInput = forwardRef<HTMLSelectElement, SelectProps>(function SelectInput(
  { error, options, placeholder, ...rest },
  ref,
) {
  return (
    <select ref={ref} aria-invalid={error ? true : undefined} {...rest}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
});

/* -------------------------------------------------------------------------- */
/* Toolbar select (filter bars, not forms)                                    */
/* -------------------------------------------------------------------------- */

interface ToolbarSelectProps<T extends string> {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  id?: string;
}

export function ToolbarSelect<T extends string>({
  label,
  value,
  onChange,
  options,
  id,
}: ToolbarSelectProps<T>) {
  const generated = useId();
  const selectId = id ?? generated;

  return (
    <div className="select">
      <label className="sr-only" htmlFor={selectId}>
        {label}
      </label>
      <select id={selectId} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Radio pills                                                                */
/* -------------------------------------------------------------------------- */

interface RadioPillsProps {
  name: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  legend: string;
}

export function RadioPills({ name, value, options, onChange, legend }: RadioPillsProps) {
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="sr-only">{legend}</legend>
      <div className="radio-pills">
        {options.map((option) => (
          <label key={option} className="radio-pill">
            <input
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/* -------------------------------------------------------------------------- */
/* Checkbox                                                                   */
/* -------------------------------------------------------------------------- */

interface CheckProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  count?: number;
  children: ReactNode;
  id?: string;
}

export function CheckRow({ checked, onChange, count, children, id }: CheckProps) {
  const generated = useId();
  const inputId = id ?? generated;

  return (
    <label className="filter-check" htmlFor={inputId}>
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="filter-check__box" aria-hidden="true">
        <Icon name="check" />
      </span>
      <span>{children}</span>
      {count !== undefined ? <span className="filter-check__count">{count}</span> : null}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Range                                                                      */
/* -------------------------------------------------------------------------- */

interface RangeProps {
  label: string;
  id: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}

export function RangeInput({ label, id, min, max, step, value, onChange }: RangeProps) {
  return (
    <>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* File upload                                                                */
/* -------------------------------------------------------------------------- */

interface FileDropProps {
  id: string;
  accept?: string;
  onFile: (file: File | null) => void;
  hint?: string;
  error?: string;
}

export function FileDrop({ id, accept, onFile, hint, error }: FileDropProps) {
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  const handle = (file: File | null) => {
    setFileName(file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB` : null);
    onFile(file);
  };

  return (
    <label
      className={`file-drop${dragging ? " is-drag" : ""}${fileName ? " is-filled" : ""}`}
      htmlFor={id}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handle(e.dataTransfer.files?.[0] ?? null);
      }}
    >
      <span className="file-drop__icon">
        <Icon name="upload" />
      </span>
      <span>
        <span className="file-drop__text">{fileName ?? "Drop your file here or click to browse"}</span>
        <br />
        <span className="file-drop__hint">{hint ?? "PDF or DOCX, up to 5 MB"}</span>
      </span>
      <input
        id={id}
        type="file"
        accept={accept}
        aria-invalid={error ? true : undefined}
        onChange={(e) => handle(e.target.files?.[0] ?? null)}
      />
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Success panel                                                              */
/* -------------------------------------------------------------------------- */

export function FormSuccess({
  title,
  message,
  children,
}: {
  title: string;
  message: ReactNode;
  children?: ReactNode;
}) {
  return (
    <motion.div
      className="form-success is-visible"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
    >
      <motion.span
        className="form-success__check"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 18, delay: 0.08 }}
      >
        <Icon name="check" />
      </motion.span>
      <h3>{title}</h3>
      <p>{message}</p>
      {children}
    </motion.div>
  );
}
